import { canAssignRole, canManageMember, type Permission } from '@elsa/contracts';
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Link,
  Loading,
  Note,
  Page,
  Select,
  Table,
  Td,
} from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { fmtDate } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, callFunction, must, rpc, supabase } from '../lib/supabase.ts';
import type { Team, TeamRole } from '../lib/types.ts';
import { CountyPicker } from './common.tsx';

// Echipa firmei (docs/23): membrii cu rolul, judetele si functia lor, invitatiile pe email
// (Edge Function invite), transferul proprietatii. Rolurile sunt ale firmei (pagina Roluri);
// cine are members.manage da doar roluri in limita drepturilor lui. Regulile sunt in SQL;
// UI-ul doar ascunde ce nu se poate.

const allCounties = (r: TeamRole | undefined) =>
  Boolean(r && (r.is_owner || r.permissions.includes('data.all_counties')));

function MemberCounties({
  value,
  editable,
  label,
  onSave,
}: {
  value: string[];
  editable: boolean;
  label: string;
  onSave: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState(value);
  const changed = draft.join() !== value.join();
  return (
    <div className="space-y-1">
      <CountyPicker value={draft} onChange={setDraft} disabled={!editable} label={label} />
      {changed ? (
        <Button onClick={() => onSave(draft)} aria-label={`Salvează ${label.toLowerCase()}`}>
          Salvează județele
        </Button>
      ) : null}
    </div>
  );
}

export function TeamPage() {
  const { org, can, session, setOrg, orgs } = useOrg();
  const qc = useQueryClient();
  const [sent, setSent] = useState<string | null>(null);
  const [inviteRole, setInviteRole] = useState('');
  const [inviteCounties, setInviteCounties] = useState<string[]>([]);
  const q = useQuery({
    queryKey: ['team', org.id],
    queryFn: () => rpc<Team>('team', { p_org: org.id }),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['team'] });
    void qc.invalidateQueries({ queryKey: ['orgs'] });
  };
  const invite = useMutation({
    mutationFn: (b: { email: string; role_id: string; counties: string[]; job_title: string }) =>
      callFunction('invite', { organization_id: org.id, ...b }),
    onSuccess: (_d, b) => {
      setSent(`Invitația a plecat la ${b.email}.`);
      setInviteCounties([]);
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: (b: { user: string; role?: string; job_title?: string; counties?: string[] }) =>
      rpc('update_member', {
        p_org: org.id,
        p_user: b.user,
        ...(b.role ? { p_role_id: b.role } : {}),
        ...(b.job_title !== undefined ? { p_job_title: b.job_title } : {}),
        ...(b.counties ? { p_counties: b.counties } : {}),
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (user: string) => rpc('remove_member', { p_org: org.id, p_user: user }),
    onSuccess: async (_d, user) => {
      if (user === session.user.id) {
        const other = orgs.find((o) => o.id !== org.id);
        if (other) setOrg(other.id);
        await qc.invalidateQueries();
        location.assign('/app');
      } else refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => must(app().from('invitations').delete().eq('id', id)),
    onSuccess: refresh,
  });
  const transfer = useMutation({
    mutationFn: (user: string) => rpc('transfer_ownership', { p_org: org.id, p_new_owner: user }),
    onSuccess: async () => {
      await supabase.auth.refreshSession();
      refresh();
    },
  });

  const t = q.data;
  const mine: Permission[] = t?.my_permissions ?? org.permissions;
  const roles = t?.roles ?? [];
  const byId = new Map(roles.map((r) => [r.id, r]));
  const assignable = roles.filter((r) => canAssignRole(mine, r));
  const chosen =
    byId.get(inviteRole) ?? assignable.find((r) => r.template === 'member') ?? assignable[0];
  const seats = typeof t?.seats === 'number' ? t.seats : null;
  const used = (t?.members.length ?? 0) + (t?.invitations.length ?? 0);
  const onInvite = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setSent(null);
    const role = byId.get(String(f.get('role')));
    invite.mutate({
      email: String(f.get('email')).trim(),
      role_id: String(f.get('role')),
      counties: allCounties(role) ? [] : inviteCounties,
      job_title: String(f.get('job_title') ?? '').trim(),
    });
    e.currentTarget.reset();
  };

  return (
    <Page title="Echipa">
      <p className="text-sm text-gri-800">
        {seats !== null
          ? `Locuri: ${t?.members.length ?? 0} ocupate din ${seats}`
          : 'Locuri nelimitate'}
        {t?.invitations.length ? `, ${t.invitations.length} invitații în așteptare` : ''}.
        {can('roles.manage') ? (
          <>
            {' '}
            Rolurile și drepturile lor le schimbi din{' '}
            <Link to="/app/roluri" className="underline">
              Roluri
            </Link>
            .
          </>
        ) : null}
      </p>
      <ErrorBox error={q.error ?? update.error ?? remove.error ?? cancel.error ?? transfer.error} />
      <Card title="Membrii">
        {q.isLoading ? (
          <Loading />
        ) : (
          <Table head={['Nume', 'Rol', 'Județe', 'Funcția', 'Din', '']}>
            {t?.members.map((m) => {
              const self = m.user_id === session.user.id;
              const role = byId.get(m.role_id);
              const manage = role ? canManageMember(mine, role, self) : false;
              return (
                <tr key={m.user_id}>
                  <Td>
                    <div className="font-medium">
                      {m.full_name ?? m.email} {self ? <Badge>tu</Badge> : null}
                    </div>
                    <div className="text-xs text-gri-700">{m.email}</div>
                  </Td>
                  <Td>
                    {manage ? (
                      <Select
                        aria-label={`Rolul lui ${m.email}`}
                        value={m.role_id}
                        className="w-auto"
                        onChange={(e) => update.mutate({ user: m.user_id, role: e.target.value })}
                      >
                        {[
                          ...new Map(
                            [...assignable, ...(role ? [role] : [])].map((r) => [r.id, r]),
                          ).values(),
                        ].map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Badge tone={m.is_owner ? 'info' : 'neutral'}>{m.role}</Badge>
                    )}
                  </Td>
                  <Td>
                    {m.all_counties ? (
                      <span className="text-sm text-gri-700">toate</span>
                    ) : (
                      <MemberCounties
                        key={m.counties.join()}
                        value={m.counties}
                        editable={manage}
                        label={`Județele lui ${m.email}`}
                        onSave={(counties) => update.mutate({ user: m.user_id, counties })}
                      />
                    )}
                  </Td>
                  <Td>
                    {self || can('members.manage') ? (
                      <Input
                        aria-label={`Funcția lui ${m.email}`}
                        defaultValue={m.job_title ?? ''}
                        maxLength={80}
                        placeholder="ex. Director achiziții"
                        onBlur={(e) => {
                          if (e.target.value.trim() !== (m.job_title ?? '')) {
                            update.mutate({ user: m.user_id, job_title: e.target.value.trim() });
                          }
                        }}
                      />
                    ) : (
                      (m.job_title ?? '—')
                    )}
                  </Td>
                  <Td className="whitespace-nowrap">{fmtDate(m.joined_at)}</Td>
                  <Td className="whitespace-nowrap">
                    <div className="flex gap-2">
                      {can('org.owner') && !self ? (
                        <Button
                          onClick={() => {
                            if (confirm(`Predai firma lui ${m.email}? Tu devii administrator.`))
                              transfer.mutate(m.user_id);
                          }}
                        >
                          Predă firma
                        </Button>
                      ) : null}
                      {manage || (self && !m.is_owner) ? (
                        <Button
                          tone="danger"
                          onClick={() => {
                            if (
                              confirm(self ? 'Ieși din firmă?' : `Scoți pe ${m.email} din firmă?`)
                            )
                              remove.mutate(m.user_id);
                          }}
                        >
                          {self ? 'Ieși din firmă' : 'Scoate'}
                        </Button>
                      ) : null}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>
      {can('members.manage') && assignable.length ? (
        <>
          <Card title="Invită un coleg">
            <form className="grid gap-3 sm:grid-cols-[1fr_12rem_1fr]" onSubmit={onInvite}>
              <Field label="Email">
                <Input name="email" type="email" required />
              </Field>
              <Field label="Rol">
                <Select
                  name="role"
                  value={chosen?.id ?? ''}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {assignable.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Funcția (opțional)">
                <Input name="job_title" maxLength={80} />
              </Field>
              {chosen && !allCounties(chosen) ? (
                <div className="space-y-1 sm:col-span-3">
                  <div className="eticheta text-gri-700">Județele lui</div>
                  <CountyPicker
                    value={inviteCounties}
                    onChange={setInviteCounties}
                    label="Județele colegului invitat"
                  />
                  <div className="text-xs text-gri-700">
                    Rolul ales vede doar achizițiile autorităților din aceste județe.
                  </div>
                </div>
              ) : null}
              <div>
                <Button tone="primary" type="submit" disabled={invite.isPending}>
                  Trimite invitația
                </Button>
              </div>
            </form>
            <div className="mt-3 space-y-2">
              <ErrorBox error={invite.error} />
              {sent ? <Note>{sent}</Note> : null}
              {seats !== null && used >= seats ? (
                <Note>
                  Toate locurile din plan sunt ocupate sau rezervate: o invitație nouă se poate
                  trimite, dar nu va putea fi acceptată până nu se eliberează un loc.
                </Note>
              ) : null}
              {chosen?.description ? (
                <p className="text-xs text-gri-700">
                  {chosen.name}: {chosen.description}.
                </p>
              ) : null}
            </div>
          </Card>
          {t?.invitations.length ? (
            <Card title="Invitații în așteptare">
              <Table head={['Email', 'Rol', 'Județe', 'Expiră', '']}>
                {t.invitations.map((i) => (
                  <tr key={i.id}>
                    <Td>{i.email}</Td>
                    <Td>{i.role}</Td>
                    <Td>{i.counties.length ? i.counties.join(', ') : '—'}</Td>
                    <Td>{fmtDate(i.expires_at)}</Td>
                    <Td className="whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          onClick={() =>
                            invite.mutate({
                              email: i.email,
                              role_id: i.role_id,
                              counties: i.counties,
                              job_title: i.job_title ?? '',
                            })
                          }
                        >
                          Retrimite
                        </Button>
                        <Button tone="danger" onClick={() => cancel.mutate(i.id)}>
                          Anulează
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            </Card>
          ) : null}
        </>
      ) : null}
    </Page>
  );
}
